import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Reveal, { SectionLabel } from "@/components/Reveal";
import { Check, ShieldCheck, Zap, Users, BookOpen, RefreshCw, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INCLUDED = [
  { icon: Zap, text: "Lifetime access to the Atlas indicator" },
  { icon: Users, text: "Entry to the private Atlas community" },
  { icon: BookOpen, text: "Full education & psychology vault" },
  { icon: RefreshCw, text: "All future indicator updates & refinements" },
];

const Pricing = () => {
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        lookup_key: "atlas_lifetime",
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Checkout could not be started. Please try again or message us on Telegram.");
      setLoading(false);
    }
  };

  return (
    <section id="join" className="relative py-24 lg:py-36 overflow-hidden" data-testid="pricing-section">
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[640px] h-[640px] rounded-full bg-gold/[0.07] blur-[130px] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <Reveal>
            <SectionLabel>Join Atlas</SectionLabel>
            <h2 className="mt-5 font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-slate-50 leading-tight" data-testid="pricing-title">
              Your next chapter starts
              <span className="text-gold-gradient"> with one decision.</span>
            </h2>
            <p className="mt-5 text-sm sm:text-base text-slate-400 leading-relaxed">
              Get access to the indicator, the analysis and a community that's serious about growth.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="max-w-xl mx-auto glass-panel gold-ring chart-frame border border-gold/30 p-6 sm:p-10" data-testid="pricing-card">
            <div className="flex items-center justify-between">
              <span className="font-mono text-[11px] tracking-[0.3em] text-gold">LIFETIME ACCESS PASS</span>
              <span className="font-mono text-[10px] tracking-widest text-slate-500 border border-line rounded-sm px-2 py-1">ONE-TIME</span>
            </div>

            <span className="mt-6 inline-flex items-center gap-2 border border-gold/40 bg-gold/10 text-gold font-mono text-[10px] tracking-[0.25em] px-3 py-1.5 rounded-sm uppercase" data-testid="pricing-intro-badge">
              Introductory Price · Rising Soon
            </span>

            <div className="mt-4 flex items-end gap-2">
              <span className="font-display font-extrabold text-5xl sm:text-6xl text-slate-50 tracking-tight" data-testid="pricing-amount">£450</span>
              <span className="text-slate-500 text-sm mb-2">GBP · once, forever</span>
            </div>

            <p className="mt-3 font-mono text-[11px] tracking-[0.15em] text-gold/80 uppercase">
              No subscriptions · No hidden fees · No signal upsells
            </p>

            <p className="mt-3 text-xs text-slate-500 leading-relaxed" data-testid="pricing-intro-note">
              One of the most affordable bank-grade indicators on the market — deliberately, for now.
              After the next few sales the price increases. Early members lock in £450 for life.
            </p>

            <ul className="mt-8 space-y-4">
              {INCLUDED.map((f, i) => (
                <li key={i} className="flex items-center gap-3.5 text-sm text-slate-300" data-testid={`pricing-feature-${i + 1}`}>
                  <span className="w-7 h-7 shrink-0 rounded-sm border border-gold/30 bg-gold/5 flex items-center justify-center">
                    <f.icon size={13} className="text-gold" />
                  </span>
                  {f.text}
                </li>
              ))}
            </ul>

            <button
              data-testid="pricing-checkout-button"
              onClick={checkout}
              disabled={loading}
              className="mt-9 w-full inline-flex items-center justify-center gap-2.5 bg-gold text-ink font-bold text-sm tracking-wide px-7 py-4 rounded-sm hover:bg-gold-bright transition-colors duration-300 disabled:opacity-60"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
              {loading ? "Preparing secure checkout…" : "Join Atlas Today"}
            </button>

            <p className="mt-4 flex items-center justify-center gap-2 text-[11px] text-slate-500">
              <ShieldCheck size={13} className="text-gold/70" />
              Secure checkout via Stripe · Instant access after payment
            </p>

            <div className="mt-6 pt-5 border-t border-line/60 text-left" data-testid="pricing-payment-methods">
              <p className="font-mono text-[10px] tracking-[0.25em] text-slate-500 uppercase mb-3">Ways To Pay</p>
              <ul className="space-y-2.5 text-xs text-slate-400 leading-relaxed">
                <li><span className="text-slate-200 font-medium">Card</span> — £450 one-time. All major cards, Apple Pay and Google Pay.</li>
                <li><span className="text-slate-200 font-medium">Clearpay</span> — 4 interest-free payments of £112.50.</li>
                <li><span className="text-slate-200 font-medium">Klarna</span> — split the payment at checkout.</li>
              </ul>
              <p className="mt-3 text-[10px] text-slate-600 leading-relaxed">
                Clearpay and Klarna are subject to eligibility, status and the provider's own checks — available
                options are shown at checkout. 18+ for instalment plans; late payments may incur provider fees.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Pricing;
